import { useState } from "react";
import { addAttribution } from "../lib";

interface ShareActionsProps {
  campaign: string;
  content: string;
  text: string;
  countryId?: string;
  className?: string;
}

function targetUrl(source: string, campaign: string, content: string, countryId?: string): string {
  const target = new URL("/", window.location.origin);
  if (countryId) target.searchParams.set("country", countryId);
  return addAttribution(target.toString(), {
    source,
    medium: "social",
    campaign,
    content,
  });
}

async function copyText(value: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const field = document.createElement("textarea");
  field.value = value;
  field.style.position = "fixed";
  field.style.opacity = "0";
  document.body.append(field);
  field.select();
  document.execCommand("copy");
  field.remove();
}

export default function ShareActions({
  campaign,
  content,
  text,
  countryId,
  className = "",
}: ShareActionsProps) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    await copyText(targetUrl("copy_link", campaign, content, countryId));
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1_800);
  };

  const shareNative = async () => {
    const url = targetUrl("native_share", campaign, content, countryId);
    if (navigator.share) {
      try {
        await navigator.share({ title: "Outbid Earth", text, url });
        return;
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") return;
      }
    }
    await copyText(url);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1_800);
  };

  const xUrl = new URL("https://twitter.com/intent/tweet");
  xUrl.searchParams.set("text", text);
  xUrl.searchParams.set("url", targetUrl("x", campaign, content, countryId));

  return (
    <div className={`share-actions ${className}`.trim()} aria-label="Share Outbid Earth">
      <button
        type="button"
        className="share-copy"
        onClick={() => void copy()}
        data-fast-goal="share_copy"
        data-fast-goal-campaign={campaign}
        data-fast-goal-content={content}
      >
        {copied ? "Link copied ✓" : "Copy challenge link"}
      </button>
      <a
        href={xUrl.toString()}
        target="_blank"
        rel="noopener noreferrer"
        data-fast-goal="share_x"
        data-fast-goal-campaign={campaign}
        data-fast-goal-content={content}
      >
        Post on X ↗
      </a>
      <button
        type="button"
        onClick={() => void shareNative()}
        data-fast-goal="share_native"
        data-fast-goal-campaign={campaign}
        data-fast-goal-content={content}
      >
        Send to a friend
      </button>
    </div>
  );
}
